import { Modal, Table } from 'antd';
import { IFarmResource } from '../../entities/farms.entities';
import { Button, Dropdown, Subtitle } from '../shared';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate, transUnit } from '../../lib/lang.helper';
import { useState } from 'react';
import { StageLabelSpan } from '../CommonViews';
import { defaultDateFormat } from '../../lib/common.helpers';
import { sendSingleRequest } from '../../apis';
import { loadFarmsData } from '../../store/farms/farms.actions';
import { showFeedback } from '../../store/ui/ui.actions';

interface IRow {
  line_id: number;
  line_name: string;
  date_seed?: string | null;
  seeding_id: number;
  stage: number;
}

interface Props {
  visible: boolean;
  farmData: IFarmResource;
  onClose: () => void;
}

const LinesBulkUpdateModal = ({ visible, farmData, onClose }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);

  const [data, setData] = useState<IRow[]>(
    farmData.lines
      .filter(
        x => !!x.growing_cycle && !!x.growing_cycle.main_seed.spat_storage,
      )
      .map(x => ({
        line_id: x.id,
        line_name: x.line_name,
        seeding_id: x.growing_cycle!.main_seed.id,
        date_seed: defaultDateFormat(
          x.growing_cycle!.main_seed.planned_date_seed,
        ),
        stage: x.growing_cycle!.main_seed.stage,
      })),
  );
  const [disabled, setDisabled] = useState(false);

  const updateForm = (lineID: number, stage: number) => {
    const newData = data.map(x => (x.line_id === lineID ? { ...x, stage } : x));
    setData(newData);
  };

  const columns = [
    {
      title: transUnit(lang, 'line', farmData.structure),
      key: 'line_name',
      render: (x: IRow) => x.line_name,
    },
    {
      title: translate(lang, 'Date Seeded'),
      key: 'date_seed',
      render: (x: IRow) => (x.date_seed ? x.date_seed : '-'),
    },
    {
      title: translate(lang, 'Batch Stage'),
      key: 'stage',
      render: (x: IRow) => (
        <Dropdown
          value={x.stage.toString()}
          options={[1, 2, 3, 4].map(x => ({
            id: x.toString(),
            value: x.toString(),
            label: <StageLabelSpan stage={x} farmId={farmData.id} />,
          }))}
          onChange={v => updateForm(x.line_id, Number(v))}
        />
      ),
    },
    {
      title: '',
      key: 'spacer',
    },
  ];

  const confirmClick = async () => {
    const payload = data.map(x => ({
      line_id: x.line_id,
      seeding_id: x.seeding_id,
      stage: x.stage,
    }));
    setDisabled(true);
    const response = await sendSingleRequest(
      { lines: payload },
      'POST',
      'api/line/bulk-update',
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(lang, '_lines_updated_ok'),
          type: 'success',
        }),
      );
      onClose();
    } else {
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessageModal: true,
          duration: -1,
          message:
            response.data?.message ?? translate(lang, '_failed_update_lines'),
          type: 'error',
        }),
      );
    }
  };

  return (
    <Modal visible={visible} onCancel={onClose} footer={null}>
      <div className='wrap'>
        <div className='mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Farm %s', farmData.name)}
          </Subtitle>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Update bulk lines')}
          </Subtitle>
        </div>
        <div className='section-modal'>
          <div className='section'>
            <Table
              rowKey={'line_id'}
              className='table table--units table--small'
              dataSource={data}
              columns={columns}
              pagination={false}
            />
          </div>
          <div className='modal-button d-flex justify-end align-center'>
            <Button
              width={'small'}
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={confirmClick}
              disabled={disabled}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default LinesBulkUpdateModal;
